import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import cx from 'clsx'

import AutoTooltip from 'components/Common/AutoTooltip'
import AutoTooltipContent from 'components/Common/AutoTooltip/AutoTooltipContent'
import FormatCurrency from 'components/Common/FormatCurrency'
import SolidExclamationCircleIcon from 'components/Luxkit/Icons/solid/SolidExclamationCircleIcon'
import AdditionalGuestsPopup from 'components/Booking/AdditionalGuestsPopup'
import Pill from 'components/Luxkit/Pill'

import { hasIncludedGuestsExceeded, getSumExtraGuestsSurcharge } from 'lib/checkout/cartReservationUtils'
import config from 'constants/config'
import TaxesAndFees from 'components/OfferPage/PackageOption/Pricing/TaxesAndFees'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE, OFFER_TYPE_VILLA } from 'constants/offer'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'
import getValueOffPercent from 'lib/getValueOffPercent'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import useHotelPriceColour from 'hooks/useHotelPriceColour'
import GeoContext from 'contexts/geoContext'
import PriceDetailsModal from 'components/Common/PriceDetails/PriceDetailsModal'
import TextButton from 'components/Luxkit/Button/TextButton'
import SecureWithDepositTagAndTooltip from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndTooltip'
import { isDepositAvailableOnOffer, isDepositEnabledForOfferType } from 'lib/payment/depositsUtils'
import noop from 'lib/noop'
import DepositTagOffersContext from 'contexts/depositTagOffersContext'
import getLPCRegionLOS from 'lib/getLPCRegionLOS'
import moment from 'moment'
import { buildSuggestedDatesParamsKey, buildSuggestedDatesString, getMinimumDurationFromFlexibleNights } from 'lib/search/searchUtils'
import { connect } from 'react-redux'
import { getSuggestedDates } from 'selectors/offerSelectors'
import ReserveForZeroTag from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroTag'
import { reserveForZeroEnabled } from 'checkout/selectors/featureConfig/reserveForZeroSelector'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import CruiseDiscountTermsAndConditions from 'components/Cruises/CruiseDiscountTermsAndConditions'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'
import useShowNewHotelSearch from 'components/SearchV2/Hooks/useShowNewHotelSearch'
import { pluralizeToString } from 'lib/string/pluralize'
import { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { EmptyArray, sum } from 'lib/array/arrayUtils'
import SolidBoltAltIcon from 'components/Luxkit/Icons/solid/SolidBoltAltIcon'
import Label from 'components/Luxkit/Label'
import Group from 'components/utils/Group'
import { getOfferExtra } from 'selectors/offerExtraSelector'
import { useAppDispatch } from 'hooks/reduxHooks'
import { fetchOfferExtraById } from 'actions/OfferActions'

const Root = styled.div`
  display: flex;
  flex-direction: column;

  &.align-left {
    align-items: flex-start;
  }

  &.align-center {
    align-items: center;
  }

  &.align-right {
    align-items: flex-end;
  }
`

export const StyledExclamationIcon = styled(SolidExclamationCircleIcon)`
  position: relative;
  margin-left: ${rem(5)};
  top: ${rem(3)};
`

interface Props {
  offer: App.Offer | App.OfferSummary;
  className?: string;
  rooms?: Array<App.Occupants>;
  pkg: App.Package;
  pricing?: App.OfferAvailableRate;
  hasDates?: boolean;
  align?: 'left' | 'center' | 'right' | 'none';
  offerUrl: string;
  checkInDate?: moment.Moment;
  flexibleMonths?: string;
  flexibleNights?: string;
  hasPackageWithDepositsEnabled?: boolean;
}

interface MappedStateProps {
  suggestedDates: App.OfferSuggestedDates
  isReserveForZeroEnabled: boolean;
  offerExtras?: App.OfferExtra;
}

function OfferTilePricing(props: Props & MappedStateProps) {
  const {
    offer,
    className,
    pkg,
    pricing,
    hasDates,
    align = 'none',
    rooms = EmptyArray,
    offerUrl,
    checkInDate,
    suggestedDates,
    flexibleNights,
    flexibleMonths,
    isReserveForZeroEnabled,
    hasPackageWithDepositsEnabled,
    offerExtras,
  } = props
  const hasSuggestedCheckInCheckOut = suggestedDates?.checkIn && suggestedDates?.checkOut

  const useNewTileDesign = useShowNewHotelSearch()

  const { addDepositTagOffer = noop } = useContext(DepositTagOffersContext) ?? {}
  const { currentCurrency, showTaxExclusivePrices } = useContext(GeoContext)
  const priceMultiplier = pricing ? 1 : (rooms?.length || 1)
  const extraGuestSurcharges = useMemo(() => getSumExtraGuestsSurcharge(pkg, (rooms ?? []), offer), [pkg, rooms, offer])
  const taxesAndFees = (pricing?.taxesAndFees ?? pkg.taxesAndFees) || 0

  const propertyFees = (pricing?.propertyFees ?? pkg.propertyFees ?? 0) * priceMultiplier
  const showPriceDetails = taxesAndFees > 0 || propertyFees > 0
  const exclusivePriceColor = useHotelPriceColour(taxesAndFees)

  let hotelPrice = (pricing ?? pkg).price + (pkg?.surcharge ?? 0)
  hotelPrice = hotelPrice * priceMultiplier + extraGuestSurcharges
  const totalPrice = suggestedDates?.price ?? (showTaxExclusivePrices ? hotelPrice : hotelPrice + propertyFees)
  const totalValue = suggestedDates?.value ?? (pkg.value * (rooms?.length || 1) + extraGuestSurcharges)
  const discountPercent = pkg.discountPercent ?? Math.round((totalValue - hotelPrice) / totalValue * 100)

  const shouldShowDiscountPercent = offer.type === OFFER_TYPE_LAST_MINUTE
  const shouldShowValue = pkg.value > 0 && totalValue > totalPrice && pkg.shouldDisplayValue &&
    !shouldShowDiscountPercent && (offer.type == OFFER_TYPE_HOTEL || !pkg.roomRate?.inclusionsHideValue)

  const showIncludedGuestsExceeded = useMemo(() => {
    if (!pkg.roomType || (pkg.roomRate?.extraGuestSurcharges?.length || 0) > 0) {
      return false
    }

    return rooms?.some(room => hasIncludedGuestsExceeded(room, pkg, offer))
  }, [pkg, rooms, offer])

  const valueOffPercent = getValueOffPercent(totalValue, hotelPrice)
  const shouldShowRate = valueOffPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  let staysFrom = offer.minDuration
  const LPCRegionLOS = getLPCRegionLOS(offer)
  if (LPCRegionLOS && offer.packages.some(p => p.duration === LPCRegionLOS)) {
    staysFrom = LPCRegionLOS
  }
  const showStaysFromLabel = !hasDates && offer.type === OFFER_TYPE_ALWAYS_ON && LPCRegionLOS && (staysFrom ?? 0) < pkg.duration

  const showSecureWithDepositTag = hasPackageWithDepositsEnabled !== undefined ? hasPackageWithDepositsEnabled :
    isDepositEnabledForOfferType(offer.type) &&
    isDepositAvailableOnOffer(offer, currentCurrency, checkInDate, totalPrice, pkg, pricing)

  useEffect(() => {
    if (showSecureWithDepositTag) {
      addDepositTagOffer(offer.id)
    }
  }, [showSecureWithDepositTag, addDepositTagOffer, offer.id])

  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(fetchOfferExtraById(offer.id))
  }, [dispatch, offer.id])

  const isReservableForZero = isReserveForZeroEnabled && isPackageEligibleForZeroReservation(offer, pkg.roomRate?.isReservableForZero, checkInDate)

  const duration = flexibleMonths && flexibleNights ? getMinimumDurationFromFlexibleNights(flexibleNights as FLEXIBLE_DURATION_RANGE) : undefined

  const numBookedToday = useMemo(() => {
    if (!offerExtras) {
      return 0
    }
    return sum(offerExtras.roomsPurchasesToday, (room) => room.purchases)
  }, [offerExtras])

  const getDurationText = useCallback(() => {
    if (hasDates) {
      return undefined
    }

    if (hasSuggestedCheckInCheckOut) {
      return buildSuggestedDatesString(suggestedDates.checkIn, suggestedDates.checkOut)
    }
    if (suggestedDates && duration) {
      return `${pluralizeToString('night', duration)} from`
    }
    if (pkg.duration && offer.type === OFFER_TYPE_VILLA) {
      return `${pluralizeToString('night', pkg.duration)} from`
    }
    if (offer.tileDurationLabel) {
      return `${offer.tileDurationLabel} from`
    }
    if (LPCRegionLOS) {
      return pluralizeToString('night', pkg.duration)
    }
    return 'From'
  }, [LPCRegionLOS, duration, hasDates, hasSuggestedCheckInCheckOut, offer.tileDurationLabel, offer.type, pkg.duration, suggestedDates])
  const isCruise = offer.holidayTypes.map(i => i.toLowerCase()).includes('cruises')
  const saleUnit = offer.saleUnit?.toLowerCase() === 'cruise' ? 'person' : offer.saleUnit
  return (
    <Root className={cx(className, `align-${align}`)}>
      <Group gap={4} direction="vertical">
        {numBookedToday > 5 && <Label variant="urgency" startIcon={<SolidBoltAltIcon/>} kind="plain">Booked {numBookedToday} times today</Label>}
        {showSecureWithDepositTag && !isReservableForZero && <SecureWithDepositTagAndTooltip
        offerType={offer.type}
      />}
        { isReservableForZero && <ReserveForZeroTag /> }
      </Group>
      <Caption variant="medium" colour="neutral-three">
        {hasDates && <>
          Total for {pluralizeToString('night', pkg.duration)} {(rooms?.length ?? 0) > 0 && `${formatOccupantsShort(rooms)} from`}
        </>}
        {getDurationText()}
      </Caption>
      <Heading data-testid="search-price" variant={useNewTileDesign ? 'heading4' : 'heading3'} colour={exclusivePriceColor}>
        <FormatCurrency value={totalPrice} format="roundedDollar" />
        {(rooms?.length ?? 0) === 0 && <Caption variant="medium" as="span" colour="neutral-two">/{saleUnit}</Caption>}
      </Heading>
      {shouldShowValue && isCruise &&
        <ValueWrapper variant="medium" weight="bold">
          <span data-testid="value-box">
            Valued up to <FormatCurrency value={Math.ceil(totalValue)} />
          </span>
          {shouldShowRate &&
            <>
              <Pill kind="primary" variant="success">-{valueOffPercent}%</Pill>
              <CruiseDiscountTermsAndConditions />
            </>
          }
        </ValueWrapper>
      }
      <TaxesAndFees
        taxesPayableAtProperty={offer.property?.taxesPayableAtProperty}
        taxAmount={taxesAndFees}
      />
      {showStaysFromLabel &&
        <Caption colour="neutral-three" variant="medium">
          Stays from {staysFrom} available
        </Caption>
      }
      {!!pkg.roomOccupancy && offer.type === 'tour' && <Caption colour="neutral-three" variant="medium">{pkg.roomOccupancy}</Caption>}
      {showIncludedGuestsExceeded && (
        <AdditionalGuestsPopup
          complex={false}
          description="+ Extra guest surcharge"
          modalContent={pkg.roomPolicyDescription}
        />
      )}
      {shouldShowValue && !isCruise &&
        <ValueWrapper variant="medium" weight="bold">
          <span data-testid="value-box">
            Valued up to <FormatCurrency value={Math.ceil(totalValue)} />
          </span>
          {shouldShowRate &&
            <Pill kind="primary" variant="success">-{valueOffPercent}%</Pill>
          }
        </ValueWrapper>
      }

      {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
        type="estimate"
        offer={offer}
        totalCost={totalPrice}
        numberOfNights={pkg.duration}
      />}

      {shouldShowDiscountPercent && discountPercent >= config.displayDiscountThreshold.lastMinute &&
        <Caption variant="medium" weight="bold" data-testid="value-box">
          {discountPercent}% off best available rate
          <AutoTooltip>
            <StyledExclamationIcon size="XS" colour="primary" />
            <AutoTooltipContent width={240}>
              Discount calculated on the best rate available for this package as reported to us by the property.{' '}
              Rate excludes the value of any package inclusions – they’re our gift to you.
            </AutoTooltipContent>
          </AutoTooltip>
        </Caption>
      }
      {showPriceDetails && <PriceDetailsModal
        offer={offer}
        duration={pkg.duration}
        taxesAndFees={taxesAndFees}
        propertyFees={propertyFees}
        hotelPrice={hotelPrice}
        rooms={rooms}
        extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
        dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property?.taxesAndFeesContent : ''}
        labelSize="small"
        cta={
          <TextButton kind="primary" fit="flex" to={offerUrl}>
            View Offer
          </TextButton>
        }
      />}
    </Root>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState, ownProps) => {
  const flexibleSearchFilterKey = buildSuggestedDatesParamsKey(ownProps.flexibleMonths, ownProps.flexibleNights, ownProps.rooms)
  return {
    suggestedDates: getSuggestedDates(appState, flexibleSearchFilterKey, ownProps.offer.id),
    isReserveForZeroEnabled: reserveForZeroEnabled(appState),
    offerExtras: getOfferExtra(appState, ownProps.offer.id),
  }
})(OfferTilePricing)
